import { useEffect, useLayoutEffect } from 'react';
import { Platform } from 'react-native';

export const ORDER_TYPE_FOCUS_TARGET_ATTR = 'data-fhs-order-type-focus-target';

const ORDER_TYPE_FOCUS_HANDOFF_EVENT = 'fhs-order-type-focus-handoff';
const FOCUS_RETRY_DELAYS_MS = [0, 16, 50, 150, 350, 600, 1000, 1500, 2500, 4000];
const FALLBACK_FOCUS_AFTER_ROUTE_CHANGE_MS = 250;

let orderTypeFocusHandoffPending = false;
let activeFocusHandoffCleanup: (() => void) | null = null;
let currentPathnameForFocusHandoff: string | null = null;
let requestedPathnameForFocusHandoff: string | null = null;
let routeChangedAt = 0;

export const orderTypeFocusTargetProps =
  Platform.OS === 'web'
    ? ({ [ORDER_TYPE_FOCUS_TARGET_ATTR]: 'true' } as const)
    : {};

export function requestOrderTypeFocusHandoff() {
  orderTypeFocusHandoffPending = true;
  requestedPathnameForFocusHandoff =
    currentPathnameForFocusHandoff ?? getCurrentBrowserPathname();
  routeChangedAt = 0;

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ORDER_TYPE_FOCUS_HANDOFF_EVENT));
  }
}

export function useOrderTypeFocusHandoff(pathname: string) {
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }

    window.addEventListener(ORDER_TYPE_FOCUS_HANDOFF_EVENT, scheduleFocusHandoff);
    return () => {
      window.removeEventListener(ORDER_TYPE_FOCUS_HANDOFF_EVENT, scheduleFocusHandoff);
      activeFocusHandoffCleanup?.();
    };
  }, []);

  useLayoutEffect(() => {
    setCurrentPathnameForFocusHandoff(pathname);
    scheduleFocusHandoff();
  }, [pathname]);
}

function setCurrentPathnameForFocusHandoff(pathname: string) {
  if (currentPathnameForFocusHandoff === pathname) {
    return;
  }

  currentPathnameForFocusHandoff = pathname;

  if (
    orderTypeFocusHandoffPending &&
    requestedPathnameForFocusHandoff &&
    pathname !== requestedPathnameForFocusHandoff
  ) {
    routeChangedAt = Date.now();
  }
}

function scheduleFocusHandoff() {
  if (!orderTypeFocusHandoffPending || Platform.OS !== 'web' || typeof window === 'undefined') {
    return;
  }

  focusOrderTypeDestinationWhenReady();
}

function focusOrderTypeDestinationWhenReady() {
  if (typeof window === 'undefined' || typeof document === 'undefined' || !document.body) {
    return;
  }

  activeFocusHandoffCleanup?.();

  let isComplete = false;
  const rafIds: number[] = [];
  const timeoutIds: number[] = [];
  const observer =
    typeof window.MutationObserver !== 'undefined'
      ? new window.MutationObserver(() => tryFocusDestination())
      : null;

  const cleanup = () => {
    observer?.disconnect();
    rafIds.forEach(id => window.cancelAnimationFrame(id));
    timeoutIds.forEach(id => window.clearTimeout(id));
    if (activeFocusHandoffCleanup === cleanup) {
      activeFocusHandoffCleanup = null;
    }
  };

  const finish = () => {
    isComplete = true;
    orderTypeFocusHandoffPending = false;
    requestedPathnameForFocusHandoff = null;
    routeChangedAt = 0;
    cleanup();
  };

  const tryFocusDestination = (forceFallback = false) => {
    if (isComplete) {
      return;
    }

    if (!orderTypeFocusHandoffPending) {
      cleanup();
      return;
    }

    const allowFallback = forceFallback || canUseFallbackFocusTarget();
    if (focusOrderTypeDestination({ allowFallback })) {
      finish();
    }
  };

  activeFocusHandoffCleanup = cleanup;

  observer?.observe(document.body, {
    attributes: true,
    attributeFilter: ['aria-hidden', 'inert', 'style', ORDER_TYPE_FOCUS_TARGET_ATTR],
    childList: true,
    subtree: true,
  });

  tryFocusDestination();
  rafIds.push(window.requestAnimationFrame(() => tryFocusDestination()));
  rafIds.push(
    window.requestAnimationFrame(() => {
      rafIds.push(window.requestAnimationFrame(() => tryFocusDestination()));
    })
  );

  FOCUS_RETRY_DELAYS_MS.forEach((delay, index) => {
    const timeoutId = window.setTimeout(() => {
      const isLastAttempt = index === FOCUS_RETRY_DELAYS_MS.length - 1;
      tryFocusDestination(isLastAttempt);
      if (isLastAttempt && orderTypeFocusHandoffPending) {
        finish();
      }
    }, delay);
    timeoutIds.push(timeoutId);
  });
}

function canUseFallbackFocusTarget() {
  if (!requestedPathnameForFocusHandoff) {
    return true;
  }

  if (
    !currentPathnameForFocusHandoff ||
    currentPathnameForFocusHandoff === requestedPathnameForFocusHandoff
  ) {
    return false;
  }

  return routeChangedAt === 0 || Date.now() - routeChangedAt >= FALLBACK_FOCUS_AFTER_ROUTE_CHANGE_MS;
}

function getCurrentBrowserPathname() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return null;
  }

  return window.location.pathname;
}

function focusOrderTypeDestination({ allowFallback }: { allowFallback: boolean }) {
  if (typeof document === 'undefined') {
    return null;
  }

  if (document.querySelector('[data-fhs-action-sheet-dialog]')) {
    return null;
  }

  const explicitTarget = findFocusableCandidate(
    `[${ORDER_TYPE_FOCUS_TARGET_ATTR}="true"], [${ORDER_TYPE_FOCUS_TARGET_ATTR}]`,
    document,
    'last'
  );

  if (explicitTarget && focusElement(explicitTarget)) {
    return explicitTarget;
  }

  if (!allowFallback) {
    return null;
  }

  const mainContent = document.getElementById('main-content');
  if (!mainContent) {
    return null;
  }

  const firstPageControl = findFocusableCandidate(
    [
      'button:not([disabled])',
      '[role="button"]:not([aria-disabled="true"])',
      '[role="tab"]:not([aria-disabled="true"])',
      '[role="link"]:not([aria-disabled="true"])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', '),
    mainContent
  );

  return firstPageControl && focusElement(firstPageControl) ? firstPageControl : null;
}

function findFocusableCandidate(
  selector: string,
  root: ParentNode = document,
  direction: 'first' | 'last' = 'first'
) {
  const candidates = Array.from(root.querySelectorAll(selector));
  const orderedCandidates = direction === 'last' ? candidates.reverse() : candidates;
  return orderedCandidates.find((candidate): candidate is HTMLElement => {
    return candidate instanceof HTMLElement && isFocusableCandidate(candidate);
  });
}

function isFocusableCandidate(element: HTMLElement) {
  if (
    element.closest(
      '[role="banner"], [data-fhs-action-sheet-dialog], [aria-hidden="true"], [inert]'
    )
  ) {
    return false;
  }

  if (element.getAttribute('aria-disabled') === 'true') {
    return false;
  }

  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  return element.getClientRects().length > 0;
}

function focusElement(element: HTMLElement) {
  element.focus({ preventScroll: true });
  return document.activeElement === element || element.contains(document.activeElement);
}
